import React, {memo, useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as Actions from '../store/actions';
import {ToggleHeader} from './Toggle';
import {dropdownNoFill, hamburger, notification} from '../assets/images/images';
import SingleDropdown from './SingleDropdown';
import AddCompanyModal from '../routes/Dashboard/AddCompanyModal';
import Modal from './Modal';
import Sidebar from './Sidebar';
import {useNavigate} from 'react-router-dom';

function Header({breadCrumbs}) {
  const live = useSelector(state => state.apps.live);
  const {loading, userDetails, user} = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(userDetails?.company);
  const [addCompanyModal, setAddCompanyModal] = useState(false);
  const [openNav, setOpenNav] = useState(false);
  const isProfiledRisk = localStorage.subdomain?.includes('profiledrisk');

  const handleSwitchCompany = useCallback(
    data => {
      if (data) {
        dispatch(Actions?.switchCompany(data?._id));
      }
    },
    [dispatch],
  );

  const wallet_balance = userDetails?.company?.wallet_balance || 0;
  const wallet_limit = userDetails?.company?.wallet_limit || 0;
  const billing_status = Object.hasOwn(
    userDetails?.company || {},
    'billing_status',
  );

  const shouldShowProfiledRiskText = () => {
    const currentPath = window.location.pathname;
    const allowedPaths = [
      '/profiles',
      '/flows',
      '/profiled-risk-overview',
      '/profiled-risk-overview',
      '/profiled-risk-events',
      '/profiled-risk-configuration',
      '/pending-cases',
    ];
    return allowedPaths.some(path => currentPath.includes(path));
  };

  const getStatusText = () => {
    if (!live && !billing_status && !shouldShowProfiledRiskText()) {
      return 'You are in sandbox mode';
    } else if (!billing_status && shouldShowProfiledRiskText()) {
      return 'Service is not enabled.';
    } else if (wallet_balance < wallet_limit) {
      return (
        <span>
          Your balance is below threshold,{' '}
          <span className="underline cursor-pointer">Kindly top up!</span>
        </span>
      );
    } else {
      return null;
    }
  };

  const statusText = getStatusText();

  const shouldShowStatusText = () => {
    const currentPath = window.location.pathname;
    const allowedPaths = [
      '/easy-lookup',
      '/easy-onboard',
      '/verifications',
      '/verification-analytics',
      '/document-analysis',
      '/address-verification',
      '/billing',
    ];
    return allowedPaths.some(path => currentPath.includes(path));
  };

  const handleStatusClick = () => {
    if (wallet_balance < wallet_limit) {
      navigate('/pricing');
    }
  };

  const customTrigger = (
    <div className="hidden cursor-pointer lg:flex items-center gap-[95px]">
      <div className="flex flex-col items-start">
        <h3 className="text-[#1A1A21] capitalize font-medium text-small">
          {`${user?.first_name || ''} ${user?.last_name?.substr(0, 1) || ''}.`}
        </h3>
        <p className="text-xs text-[#8C94A6] font-normal">
          {userDetails?.company?.name}
        </p>
      </div>
      <img src={dropdownNoFill} alt="dropdown icon" />
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-30 text-white bg-white flex items-center justify-between py-2 px-8 sm:border-b sm:border-[#E4E4E6] lg:ml-[16.4%] print:hidden">
      <AddCompanyModal
        open={addCompanyModal}
        setOpen={() => setAddCompanyModal(!addCompanyModal)}
      />
      <div className="text-sm text-body">{breadCrumbs}</div>
      <div className="flex ml-auto">
        <div className="items-center hidden sm:flex">
          <span
            role="switch"
            className={`text-small font-medium uppercase py-1 px-4 rounded-full mr-2 ${
              live
                ? 'bg-[#F6F9F5] text-[#55A249]'
                : 'bg-[#F8EBEA] text-[#B42318]'
            } font-medium`}
          >
            {live ? 'live' : 'sandbox'}
          </span>
          <ToggleHeader />
        </div>

        {/* mobile menu */}
        <div className="flex items-center gap-2 sm:mr-4 lg:hidden">
          <div className="p-2 rounded bg-white80" role="button" tabIndex="0">
            <img src={notification} alt="" width={18} height={18} />
          </div>

          <button
            onClick={() => setOpenNav(!openNav)}
            className="flex items-center gap-2 px-3 py-2 rounded bg-white80"
          >
            <span className="pt-[3px] text-sm font-medium text-body">Menu</span>
            <img src={hamburger} alt="" width={18} height={18} />
          </button>

          <Modal
            enterFrom="opacity-0 -translate-x-40"
            leaveFrom="opacity-0 -translate-x-40"
            leaveTo="opacity-0 -translate-x-40"
            extraModalClasses="max-w-[352px]"
            show={openNav}
            onClose={setOpenNav}
            setOpen={setOpenNav}
            modalPosition="items-normal justify-start lg:hidden"
            modalPadding="pl-0"
          >
            <Sidebar openNav={openNav} setOpenNav={setOpenNav} />
          </Modal>
        </div>

        <div className="flex items-center">
          <div className="flex items-center gap-2 mr-2">
            <div className="hidden ml-1 lg:ml-10 md:flex items-center bg-[#F2F6FD] justify-center w-[34px] h-[34px] rounded-full">
              <p className="text-sm font-medium text-brandBlue uppercase">{`${
                user?.first_name?.substr(0, 1) || '-'
              }${user?.last_name?.substr(0, 1) || ''}`}</p>
            </div>
          </div>

          <div className="hidden md:block">
            <SingleDropdown
              dropdownData={userDetails?.companies || []}
              selected={selected}
              setSelected={data => {
                setSelected(data);
                handleSwitchCompany(data);
              }}
              spanText={
                userDetails?.company?.name ||
                selected?.name ||
                'No company selected'
              }
              selectedId={userDetails?.company?.id}
              width="w-[242px]"
              customTrigger={customTrigger}
            />
          </div>
        </div>
      </div>
      {loading ||
      !statusText ||
      !(
        shouldShowStatusText() ||
        (shouldShowProfiledRiskText() && !isProfiledRisk)
      ) ? null : (
        <div
          onClick={handleStatusClick}
          className="items-end justify-center h-[1px] w-full absolute left-0 bottom-0 bg-status-404 z-[-1] hidden lg:flex cursor-pointer"
        >
          <p className="text-status-404 text-sm font-medium px-4 pt-[5px] pb-[3px] rounded-t bg-statusBg-404 border-b">
            {statusText}
          </p>
        </div>
      )}
    </header>
  );
}

export default memo(Header);
